import { createRoot, type Root } from 'react-dom/client';
import 'leaflet/dist/leaflet.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import { App } from './App';
import { setApiBase } from './utils/api';
import { resolveLang, setLang, watchLang, whenDataReady, type Lang } from './i18n';

export interface InvestmentsWidgetProps {
  apiBaseUrl: string;
  /**
   * Canonical URL of the host page, for the Dataset markup the widget injects.
   * Optional: without it the widget falls back to the page's own canonical link
   * and then to origin+pathname. Pass it when the host page's address differs
   * from where the widget renders (staging, a preview, a mirrored path).
   */
  pageUrl?: string;
  /**
   * Display language. Optional: the auto-mount resolves it from data-lang,
   * then window.language (set by the DZ website), then <html lang>; German
   * when none says otherwise.
   */
  lang?: Lang;
}

// Mount the widget into a host-page container. Returns a handle for cleanup.
// In English the data-name translations (federal states, industries,
// spotlights; i18n/data.ts) are loaded first, so the first paint is already
// English. Without an explicit `lang` prop the widget follows the host page
// live (watchLang): a switch unmounts it and mounts it again in the new
// language. A full unmount, not a keyed re-render, so the old tree's pending
// URL write lands before the new tree reads the URL back; viewCarry keeps the
// rest.
export function render(container: HTMLElement, props: InvestmentsWidgetProps) {
  setApiBase(props.apiBaseUrl);
  const viewCarry: Record<string, unknown> = {};
  let root: Root | null = null;
  const mount = (lang: Lang) => {
    root?.unmount();
    setLang(lang);
    root = createRoot(container);
    root.render(<App apiBaseUrl={props.apiBaseUrl} pageUrl={props.pageUrl} viewCarry={viewCarry} />);
  };
  const show = (lang: Lang) => whenDataReady(lang, props.apiBaseUrl, () => mount(lang));
  let cancelPending = show(props.lang ?? resolveLang(container));

  const stopWatching = props.lang
    ? () => {}
    : watchLang(container, (lang) => {
        cancelPending();
        cancelPending = show(lang);
      });

  return {
    unmount: () => {
      cancelPending();
      stopWatching();
      root?.unmount();
      root = null;
    },
  };
}

// Auto-initialize if a `[data-investments-widget]` element is present and
// carries a `data-api-url` attribute. Lets the host page embed with two tags:
//   <div data-investments-widget data-api-url="https://api.../api"></div>
//   <script src=".../investments-widget.umd.js"></script>
// An optional data-page-url pins the canonical URL used in the Dataset markup;
// an optional data-lang ("de" | "en") overrides window.language.
if (typeof window !== 'undefined') {
  const autoMount = () => {
    const containers = document.querySelectorAll('[data-investments-widget]');
    containers.forEach((container) => {
      const apiBaseUrl = container.getAttribute('data-api-url');
      if (apiBaseUrl) {
        render(container as HTMLElement, {
          apiBaseUrl,
          pageUrl: container.getAttribute('data-page-url') ?? undefined,
        });
      }
    });
  };

  // DOMContentLoaded has already fired if the host loads us with defer/async or
  // injects the tag later — in that case the listener would never run and the
  // widget would silently never mount.
  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', autoMount);
  } else {
    autoMount();
  }
}
